import { ContentPageNav } from "@/components/ContentPageNav";
import { Footer } from "@/components/Footer";
import { Card } from "@/components/ui/card";
import { Star, Quote } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useNavigate } from "@/lib/router-compat";

interface Review {
  quote: string;
  author: string;
  role: string;
  stars: number;
  highlight?: boolean;
  skeptical?: boolean;
}

export const reviews: Review[] = [
  {
    quote: "Nu hoef ik niet steeds 5 minuten terug te lopen naar het clubhuis, ik kan meteen naar de juiste baan.",
    author: "Tennisser",
    role: "Lid bij een tennisclub",
    stars: 5,
  },
  {
    quote: "Ideaal, dit bespaart onze club een hoop vrijwilligers om in te zetten nu de indelingen automatisch worden gemaakt.",
    author: "Bestuurslid",
    role: "Tennisclub bestuur",
    stars: 5,
    highlight: true,
  },
  {
    quote: "Handig dat je het gewoon op je telefoon kan gebruiken.",
    author: "Speler",
    role: "Recreatief tennisser",
    stars: 4,
  },
  {
    quote: "Ik kan mezelf thuis al aanmelden, en dan rustig naar de club fietsen.",
    author: "Lid",
    role: "Vaste husselaar",
    stars: 5,
  },
  {
    quote: "Onze husselaars waren eerst sceptisch, maar na een paar keer gebruiken moesten ze toch wel inzien dat het een mooie vooruitgang is.",
    author: "Husselcoördinator",
    role: "Club organisator",
    stars: 5,
    highlight: true,
  },
  {
    quote: "Het is een mooi modern systeem en werkt goed voor jong & oud.",
    author: "Commissielid",
    role: "Technische commissie",
    stars: 5,
  },
  {
    quote: "Doe mij maar handmatige indelingen door een persoon die ik ken, ik vind het persoonlijk contact heel fijn, en nu moet ik met zo'n app gaan lopen pielen.",
    author: "Klassieke tennisser",
    role: "Senioren husseler",
    stars: 2,
    skeptical: true,
  },
  {
    quote: "Als bestuur hoeven we nu niet meer elke week iemand te regelen die de indeling wil maken. Dat scheelt enorm.",
    author: "Voorzitter",
    role: "Clubbestuur",
    stars: 5,
  },
  {
    quote: "Mijn vrouw regelt thuis alles digitaal, maar ik had even wat moeite met de app. Na twee keer oefenen ging het prima.",
    author: "Jan",
    role: "Recreatief speler, 68 jaar",
    stars: 4,
  },
  {
    quote: "Eindelijk geen gedoe meer met pasjes en wachten bij het prikbord. Gewoon op je telefoon kijken waar je moet spelen.",
    author: "Dubbelpartner",
    role: "Competitie- en husseler",
    stars: 5,
  },
];

const StarRating = ({ count }: { count: number }) => (
  <div className="flex gap-0.5">
    {Array.from({ length: 5 }).map((_, i) => (
      <Star
        key={i}
        className={`h-4 w-4 ${i < count ? "fill-amber-400 text-amber-400" : "text-muted-foreground/30"}`}
      />
    ))}
  </div>
);

const Reviews = () => {
  const navigate = useNavigate();

  const avgStars = (reviews.reduce((sum, r) => sum + r.stars, 0) / reviews.length).toFixed(1);

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <ContentPageNav showPricing />

      <main className="flex-1">
        {/* Hero */}
        <section className="py-10 md:py-24 px-3">
          <div className="container mx-auto max-w-4xl text-center">
            <h1 className="text-3xl md:text-5xl font-bold text-foreground mb-4">
              Wat zeggen spelers & clubs?
            </h1>
            <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto mb-6">
              Ervaringen van tennissers en clubs die De Hussel App gebruiken voor hun husselavonden.
            </p>
            <div className="flex items-center justify-center gap-2 text-lg">
              <StarRating count={5} />
              <span className="font-semibold text-foreground">{avgStars}</span>
              <span className="text-muted-foreground">gemiddeld ({reviews.length} reviews)</span>
            </div>
          </div>
        </section>

        {/* Reviews grid */}
        <section className="pb-14 md:pb-20 px-3">
          <div className="container mx-auto max-w-5xl">
            <div className="columns-1 md:columns-2 gap-6 space-y-6">
              {reviews.map((review, index) => (
                <Card
                  key={index}
                  className={`break-inside-avoid p-6 ${
                    review.skeptical
                      ? "border-muted bg-muted/30"
                      : review.highlight
                        ? "border-primary/30 bg-primary/5"
                        : "bg-card"
                  }`}
                >
                  <div className="flex items-start gap-3 mb-3">
                    <Quote className={`h-8 w-8 flex-shrink-0 mt-1 ${review.skeptical ? "text-muted-foreground/40" : "text-primary/40"}`} />
                    <p className={`text-base leading-relaxed ${review.skeptical ? "text-muted-foreground italic" : "text-foreground"}`}>
                      "{review.quote}"
                    </p>
                  </div>
                  <div className="flex items-center justify-between mt-4 pt-3 border-t border-border/50">
                    <div>
                      <p className="font-medium text-sm text-foreground">— {review.author}</p>
                      <p className="text-xs text-muted-foreground">{review.role}</p>
                    </div>
                    <StarRating count={review.stars} />
                  </div>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-10 md:py-16 px-3 bg-muted/30">
          <div className="container mx-auto max-w-2xl text-center">
            <h2 className="text-xl md:text-2xl font-bold text-foreground mb-4">
              Benieuwd? Probeer het zelf.
            </h2>
            <p className="text-muted-foreground mb-6">
              Registreer je club en ervaar hoe automatische hussel-indelingen werken.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button size="lg" onClick={() => navigate("/club-register")}>
                Club registreren
              </Button>
              <Button size="lg" variant="outline" onClick={() => navigate("/demo")}>
                Demo aanvragen
              </Button>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default Reviews;
